import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import {
  MagnifyingGlass, TreeStructure, CheckCircle, Clock, WarningCircle,
  XCircle, CircleDashed, ArrowRight, CaretDown, CaretUp,
} from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Oct 2026, user's explicit ask - an SAP-GUI-style "Document Flow" view:
// search any document ID, see the ordered chain of related documents it
// touches, each with a live status. Stock Transfer Orders (STO-xxxxxx,
// full outbound+inbound lifecycle) and Supplier Shipments/GRNs
// (Sxxxxxx) are wired today - see document_flow_service.py's docstring
// for how the next transaction type is added. This page itself is
// already generic (renders whatever `nodes` the backend returns), so
// no frontend change is needed to plug in a new doc type.
const STATE_STYLES = {
  success: { icon: CheckCircle, color: "#12B76A", bg: "#ECFDF3", border: "#ABEFC6", label: "text-[#027A48]" },
  pending: { icon: Clock, color: "#F79009", bg: "#FFFAEB", border: "#FEDF89", label: "text-[#B54708]" },
  warning: { icon: WarningCircle, color: "#F79009", bg: "#FFFAEB", border: "#FEDF89", label: "text-[#B54708]" },
  error: { icon: XCircle, color: "#F04438", bg: "#FEF3F2", border: "#FECDCA", label: "text-[#B42318]" },
  not_started: { icon: CircleDashed, color: "#98A2B3", bg: "#F9FAFB", border: "#EAECF0", label: "text-[#667085]" },
};

function formatTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString();
}

// Oct 2026, user's explicit ask - raw SAP Goods Movement/Order IDs are
// 35-digit zero-padded (e.g. "00000000000000000000000000000285380").
// Truncate the leading zeros for display but keep the full number
// available on hover, same convention as GrnApprovalPage's
// `shortMovementId` - here as a single generic formatter since these
// IDs can appear standalone or embedded inside a longer status string
// (e.g. "Done (GM 00000000000000000000000000000285380)").
function formatSapId(text) {
  if (text === null || text === undefined) return text;
  const str = String(text);
  const parts = str.split(/(0{4,}\d+)/g);
  if (parts.length === 1) return str;
  return parts.map((part, i) =>
    /^0{4,}\d+$/.test(part) ? (
      <span key={i} title={part} className="border-b border-dotted border-current cursor-help">
        {part.replace(/^0+(?=\d)/, "") || part}
      </span>
    ) : (
      part
    )
  );
}

function DocNode({ node, expanded, onToggle, testIndex }) {
  const style = STATE_STYLES[node.state] || STATE_STYLES.not_started;
  const Icon = style.icon;
  const detailEntries = Object.entries(node.details || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <div className="flex flex-col items-stretch shrink-0 w-[220px]" data-testid={`document-flow-node-${testIndex}`}>
      <button
        onClick={onToggle}
        className="text-left rounded-lg border-2 px-3 py-3 transition-all hover:shadow-md"
        style={{ backgroundColor: style.bg, borderColor: style.border }}
        data-testid={`document-flow-node-toggle-${testIndex}`}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <Icon size={20} weight="fill" style={{ color: style.color }} />
          <span className="text-[11px] uppercase tracking-wide font-semibold text-[#667085]">{node.title}</span>
        </div>
        <div className="text-sm font-bold text-[#1D2939] font-heading truncate" title={node.doc_id || "-"}>
          {node.doc_id ? formatSapId(node.doc_id) : "\u2014"}
        </div>
        {node.subtitle && (
          <div className="text-xs text-[#667085] truncate mt-0.5" title={node.subtitle}>{formatSapId(node.subtitle)}</div>
        )}
        <div className={`text-xs font-semibold mt-1.5 flex items-center gap-1 ${style.label}`}>
          {node.status_label}
          {detailEntries.length > 0 && (expanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)}
        </div>
      </button>

      {expanded && (
        <div className="mt-2 bg-white border border-[#EAECF0] rounded-lg p-3 text-xs space-y-1.5" data-testid={`document-flow-node-details-${testIndex}`}>
          {node.timestamp && (
            <div className="flex justify-between gap-2">
              <span className="text-[#98A2B3]">Timestamp</span>
              <span className="text-[#1D2939] font-medium text-right">{formatTime(node.timestamp)}</span>
            </div>
          )}
          {detailEntries.length === 0 ? (
            <div className="text-[#98A2B3]">No further details recorded.</div>
          ) : (
            detailEntries.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-2">
                <span className="text-[#98A2B3] shrink-0">{label}</span>
                <span className="text-[#1D2939] font-medium text-right break-words">{formatSapId(String(value))}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function DocumentFlowPage() {
  const [docId, setDocId] = useState("");
  const [loading, setLoading] = useState(false);
  const [flow, setFlow] = useState(null);
  const [error, setError] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const search = async () => {
    if (!docId.trim()) return;
    setLoading(true);
    setError(null);
    setFlow(null);
    setExpandedIndex(null);
    try {
      const { data } = await axios.get(`${API}/document-flow/${encodeURIComponent(docId.trim())}`);
      setFlow(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load this document.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-[#0E7C86] px-4 sm:px-6 py-3 flex items-center gap-3">
        <TreeStructure size={22} weight="bold" className="text-white shrink-0" />
        <span className="text-white font-heading font-bold text-base shrink-0">Materials Hub</span>
        <NavTabs />
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-[#1D2939] font-heading" data-testid="document-flow-title">
            Document Flow
          </h1>
          <p className="text-sm text-[#667085] font-sans mt-0.5">
            Search any document ID to trace the full chain of related documents and their status, SAP-GUI style.
            Currently supported: Stock Transfer Orders (e.g. STO-000123, full outbound+inbound lifecycle) and Supplier Shipments/GRNs (e.g. S000123).
          </p>
        </div>

        <div className="flex gap-2 mb-6">
          <Input
            value={docId}
            onChange={(e) => setDocId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="e.g. STO-000695 or S000269"
            className="max-w-[280px]"
            data-testid="document-flow-search-input"
          />
          <Button onClick={search} disabled={loading || !docId.trim()} data-testid="document-flow-search-button" className="gap-1.5">
            <MagnifyingGlass size={14} weight="bold" />
            {loading ? "Searching..." : "Search"}
          </Button>
        </div>

        {error && (
          <div className="bg-white border border-[#D0D5DD] rounded-md px-4 py-6 text-center text-sm text-[#667085]" data-testid="document-flow-error">
            {error}
          </div>
        )}

        {flow && (
          <div className="bg-white border border-[#D0D5DD] rounded-md p-5" data-testid="document-flow-result">
            <div className="text-xs text-[#98A2B3] mb-4 uppercase tracking-wide font-semibold">
              {flow.doc_type === "sto" ? "Stock Transfer Order Flow" : flow.doc_type === "grn" ? "Supplier Shipment / GRN Flow" : flow.doc_type} &middot; {flow.doc_id}
            </div>
            <div className="overflow-x-auto pb-2">
              <div className="flex items-start gap-0 min-w-max">
                {flow.nodes.map((node, i) => (
                  <div key={i} className="flex items-start">
                    <DocNode
                      node={node}
                      testIndex={i}
                      expanded={expandedIndex === i}
                      onToggle={() => setExpandedIndex(expandedIndex === i ? null : i)}
                    />
                    {i < flow.nodes.length - 1 && (
                      <ArrowRight size={18} weight="bold" className="text-[#98A2B3] shrink-0 mt-6 mx-2" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
